import React from 'react';
import { FileText, Zap, BookOpen, AlertTriangle, CheckCircle2, Gift, Settings, ShieldCheck, CreditCard } from 'lucide-react';
import { Accordion } from './components/Accordion';
import { BonusGrid } from './components/BonusGrid';
import { PurchasePopup } from './components/PurchasePopup';
import { CountdownTimer } from './components/CountdownTimer';
import { ComparisonTable } from './components/ComparisonTable';
import { MetaPixel } from './components/MetaPixel';
import { ItalianTestimonials, ArgentinianTestimonials } from './components/Testimonials';
import { FAQ } from './components/FAQ';
const CHECKOUT_LINK = 'https://checkout.ebookpoint.shop/cart/49248776061180:1';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/2yfJkovetTmTran5cxQwAV/ChatGPT_Image_Jul_4__2026__05_02_26_PM-removebg-preview.png';
const modules = [{
  title: '🎾 1. Fondamenta: impugnatura, postura, lettura della palla, differenze destro/mancino'
}, {
  title: '💥 2. Tecnica dei colpi: diritto, rovescio, volée, bandeja, vibora, smash, globo'
}, {
  title: '🎯 3. Servizio e risposta'
}, {
  title: '🧠 4. Tattica individuale: costruzione del punto, angoli, pareti'
}, {
  title: '🤝 5. Tattica in coppia: posizionamento, comunicazione, diagonali'
}, {
  title: '♟️ 6. Giocate e schemi avanzati (X, contropiede, doppio smash, trappole tattiche)'
}, {
  title: '🏋️ 7. Preparazione fisica specifica: forza, agilità, pliometria, periodizzazione'
}, {
  title: '🛡️ 8. Prevenzione infortuni (generale): epicondilite, caviglia, ginocchio, lombalgia, riscaldamento/defaticamento'
}, {
  title: '🧘 9. Psicologia e mentalità competitiva'
}, {
  title: '🥗 10. Nutrizione specifica per padel'
}, {
  title: '💪 11. Lesioni di gomito e polso: epicondilite, epitrocleite, tendinopatie, tunnel carpale, protocollo di rafforzamento e ritorno al gioco'
}, {
  title: '🦵 12. Lesioni di caviglia e ginocchio: distorsioni, instabilità cronica, tendinopatia rotulea, lesioni meniscali, calzature, ritorno al gioco'
}];
const bonuses = [{
  title: '⏱️ Checklist Riscaldamento Express — PDF',
  image: 'https://cdn.magicpatterns.com/uploads/bK9c3XBj7pGPK6PHcEDozx/Bono_1_Italia.png'
}, {
  title: '📅 Planner Settimanale di Allenamento — Excel',
  image: 'https://cdn.magicpatterns.com/uploads/whNz66R2pKffGrPbp9GvoN/Bono_2_.png'
}, {
  title: '📋 Guida Rapida agli Schemi Tattici — PDF',
  image: 'https://cdn.magicpatterns.com/uploads/kjrbJ5mJLVbm4oFyVZmxix/Bono_3.png'
}, {
  title: '📓 Diario delle Partite — Excel',
  image: 'https://cdn.magicpatterns.com/uploads/kj6nvc15SrbRPk9RgapP83/Bono_4_.png'
}, {
  title: '📖 Glossario del Padel — PDF',
  image: 'https://cdn.magicpatterns.com/uploads/m8JvYbaC1tQHbUa5sDoGeK/Bono_5.png'
}, {
  title: '🏠 10 Esercizi Anti-Infortunio da Fare a Casa — PDF',
  image: 'https://cdn.magicpatterns.com/uploads/5iCzSZYGGyVFMMYUFEiuQC/Bono_6_.png'
}, {
  title: '🍎 Guida Nutrizionale Rapida — PDF',
  image: 'https://cdn.magicpatterns.com/uploads/5giBrQLNHZqGgeKv9d65DU/Bono_7.png'
}, {
  title: '🧘 Mental Training: Routine di Respirazione — PDF',
  image: 'https://cdn.magicpatterns.com/uploads/p7eVrhWMUZ1yut7a37URr9/bono_8.png'
}];
const problems = ['Dolori costanti a gomito o ginocchio dopo ogni partita.', 'Frustrazione per non riuscire a chiudere i punti decisivi.', 'Mancanza di intesa e comunicazione con il proprio compagno.', 'Stallo tecnico: giochi da mesi ma non vedi miglioramenti.', 'Paura di infortunarti gravemente e dover smettere di giocare.'];
function scrollToOffer() {
  const el = document.getElementById('offerta');
  if (el) el.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
const CTA_CLASS = 'block w-full max-w-md mx-auto bg-green-500 hover:bg-green-600 text-white font-black text-lg md:text-xl py-4 px-6 rounded-xl text-center shadow-[0_6px_0_rgb(21,128,61)] hover:shadow-[0_4px_0_rgb(21,128,61)] hover:translate-y-[2px] transition-all uppercase tracking-wide';
function CTAButton({
  text,
  className = '',
  checkout = false
}: {
  text: string;
  className?: string;
  checkout?: boolean;
}) {
  if (checkout) {
    return <a href={CHECKOUT_LINK} className={`${CTA_CLASS} ${className}`} data-id="element-0">
        {text}
      </a>;
  }
  return <button type="button" onClick={scrollToOffer} className={`${CTA_CLASS} ${className}`} data-id="element-1">
      {text}
    </button>;
}
export function App() {
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800 selection:bg-blue-200" data-id="element-2">
      <MetaPixel pixelId="1252242806993118" data-id="element-3" />
      <PurchasePopup data-id="element-4" />

      {/* HEADER SECTION */}
      <header className="bg-blue-900 text-white pt-10 pb-16 px-4 text-center relative overflow-hidden" data-id="element-5">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" data-id="element-6"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-7">
          <h1 className="text-2xl md:text-4xl font-black leading-tight mb-6 uppercase tracking-tight" data-id="element-8">
            Tecnica, Tattica e Prevenzione degli Infortuni in un Unico Kit
          </h1>

          <button type="button" onClick={scrollToOffer} className="block max-w-[85%] mx-auto mb-8 hover:scale-105 transition-transform w-full" data-id="element-9">
            <img src={PRODUCT_IMAGE} alt="Padel 360 Senza Infortuni" className="w-full h-auto drop-shadow-2xl rounded-lg" data-id="element-10" />
          </button>

          <h2 className="text-lg md:text-xl font-medium text-blue-100 mb-8 max-w-2xl mx-auto leading-snug" data-id="element-11">
            Il metodo che unisce tecnica, tattica, fisico e prevenzione degli
            infortuni — tutto ciò che ti serve, senza cercare in mille posti.
          </h2>

          <div className="flex flex-wrap justify-center gap-4 mb-10" data-id="element-12">
            <span className="bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full font-bold flex items-center gap-2" data-id="element-13">
              <FileText className="w-5 h-5 text-blue-300" data-id="element-14" /> 100% PDF
            </span>
            <span className="bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full font-bold flex items-center gap-2" data-id="element-15">
              <Zap className="w-5 h-5 text-yellow-400" data-id="element-16" /> Consegna Immediata
            </span>
            <span className="bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full font-bold flex items-center gap-2" data-id="element-17">
              <BookOpen className="w-5 h-5 text-green-400" data-id="element-18" /> 12 Moduli + 8
              Bonus
            </span>
          </div>

          <CTAButton text="Voglio migliorare il mio gioco ora" data-id="element-19" />
        </div>
      </header>

      {/* PROBLEMS SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-20">
        <div className="max-w-3xl mx-auto" data-id="element-21">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-8 text-gray-900" data-id="element-22">
            Probabilmente ti è successo qualcosa di questo:
          </h2>

          <div className="space-y-4 mb-12" data-id="element-23">
            {problems.map((problem, i) => <div key={i} className="flex items-start gap-4 p-4 bg-red-50 rounded-lg border border-red-100" data-id="element-24">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-25" />
                <p className="font-medium text-gray-800 text-lg" data-id="element-26">{problem}</p>
              </div>)}
          </div>

          <div className="text-center mb-10" data-id="element-27">
            <h2 className="text-3xl md:text-4xl font-black text-blue-900 mb-4" data-id="element-28">
              Per questo è nato Padel 360 Senza Infortuni
            </h2>
            <p className="text-gray-600 text-lg" data-id="element-29">
              Scopri cosa troverai all'interno del programma completo:
            </p>
          </div>

          <Accordion items={modules} data-id="element-30" />
        </div>
      </section>

      {/* ITALIAN REVIEWS */}
      <section className="py-12 px-4 bg-gray-50 border-y border-gray-200" data-id="element-31">
        <div className="max-w-5xl mx-auto" data-id="element-32">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-8 text-gray-900" data-id="element-33">
            Cosa dicono i giocatori in Italia
          </h2>
          <ItalianTestimonials data-id="element-34" />
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-4 bg-blue-900 text-white" data-id="element-35">
        <div className="max-w-3xl mx-auto" data-id="element-36">
          <div className="text-center mb-10" data-id="element-37">
            <Gift className="w-16 h-16 mx-auto text-yellow-400 mb-4" data-id="element-38" />
            <h2 className="text-2xl md:text-3xl font-black mb-4" data-id="element-39">
              Sappiamo quanto può essere difficile il padel, per questo oggi ti
              regaliamo:
            </h2>
            <p className="text-yellow-400 font-bold text-xl uppercase tracking-wide" data-id="element-40">
              Questi bonus sono GRATIS solo per oggi!
            </p>
          </div>

          <div className="mb-12" data-id="element-41">
            <BonusGrid bonuses={bonuses} data-id="element-42" />
          </div>

          <ComparisonTable data-id="element-43" />
        </div>
      </section>

      {/* ARGENTINIAN REVIEWS */}
      <section className="py-16 px-4 bg-white" data-id="element-44">
        <div className="max-w-5xl mx-auto" data-id="element-45">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-8 text-gray-900" data-id="element-46">
            Il metodo approvato dai giocatori argentini
          </h2>
          <ArgentinianTestimonials data-id="element-47" />
        </div>
      </section>

      {/* FINAL OFFER SECTION */}
      <section id="offerta" className="py-16 px-4 bg-gray-50 border-t border-gray-200 scroll-mt-4" data-id="element-48">
        <div className="max-w-xl mx-auto" data-id="element-49">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8 relative overflow-hidden" data-id="element-50">
            <div className="absolute top-0 left-0 w-full h-2 bg-green-500" data-id="element-51"></div>

            <CountdownTimer data-id="element-52" />

            <h2 className="text-xl font-black text-center text-gray-900 mb-4" data-id="element-53">
              Riepilogo dell'Ordine
            </h2>

            <div className="rounded-xl border border-gray-200 bg-gray-50/60 divide-y divide-gray-100 mb-4 overflow-hidden" data-id="element-54">
              <div className="flex justify-between items-center gap-3 p-3.5" data-id="element-55">
                <span className="flex items-center gap-2.5 font-semibold text-gray-800" data-id="element-56">
                  <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-57">
                    <BookOpen className="w-4 h-4 text-blue-600" data-id="element-58" />
                  </span>
                  Padel 360 Senza Infortuni (PDF)
                </span>
                <span className="font-bold text-gray-400 line-through whitespace-nowrap" data-id="element-59">
                  €82,99
                </span>
              </div>
              <div className="flex justify-between items-center gap-3 p-3.5 bg-green-50/50" data-id="element-60">
                <span className="flex items-center gap-2.5 font-semibold text-gray-800" data-id="element-61">
                  <span className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-62">
                    <Gift className="w-4 h-4 text-green-600" data-id="element-63" />
                  </span>
                  8 Bonus Esclusivi
                </span>
                <span className="text-xs font-black text-green-700 bg-green-100 px-2.5 py-1 rounded-full whitespace-nowrap" data-id="element-64">
                  GRATIS
                </span>
              </div>
              <div className="flex justify-between items-center gap-3 p-3.5" data-id="element-65">
                <span className="flex items-center gap-2.5 font-semibold text-gray-800" data-id="element-66">
                  <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-67">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" data-id="element-68" />
                  </span>
                  Accesso a vita + aggiornamenti
                </span>
                <span className="text-xs font-black text-blue-700 bg-blue-100 px-2.5 py-1 rounded-full whitespace-nowrap" data-id="element-69">
                  INCLUSO
                </span>
              </div>
            </div>

            <div className="text-center mb-6" data-id="element-70">
              <p className="text-gray-500 line-through text-xl mb-1" data-id="element-71">
                Prezzo normale: €82,99
              </p>
              <div className="flex items-center justify-center gap-2" data-id="element-72">
                <span className="text-5xl font-black text-green-600" data-id="element-73">
                  €27,00
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2" data-id="element-74">
                Pagamento unico. Nessun abbonamento.
              </p>
            </div>

            <CTAButton text="Sì, voglio l'offerta completa ora" className="mb-4" checkout data-id="element-75" />

            <div className="flex justify-center items-center gap-4 text-gray-400 mt-6" data-id="element-76">
              <ShieldCheck className="w-6 h-6" data-id="element-77" />
              <CreditCard className="w-6 h-6" data-id="element-78" />
              <span className="text-xs font-medium uppercase" data-id="element-79">
                Pagamento 100% Sicuro
              </span>
            </div>
          </div>

          <FAQ data-id="element-80" />

          <div className="mt-12 text-center flex flex-col items-center justify-center gap-2 text-gray-500" data-id="element-81">
            <Settings className="w-8 h-8 text-gray-400" data-id="element-82" />
            <p className="font-medium" data-id="element-83">Supporto Clienti</p>
            <a href="mailto:EbookDreamShop@gmail.com" className="text-blue-600 font-bold hover:underline" data-id="element-84">
              EbookDreamShop@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>;
}